import { useMemo } from "react";
import { Link } from "react-router";
import { ChevronRightCircle } from "lucide-react";

import { cn } from "@ui/shadCN/lib/utils";
import { Button } from "@ui/shadCN/component/button";
import { LOCALE } from "@config/global";
import { TAG_COLORS } from "@config/monitoring";

import { useObservationsCTX } from "pages/monitoring/hooks/useObservationsCTX";
import { TagsRender } from "pages/monitoring/ui/TagsRender";
import type { ObservationMetadata } from "pages/monitoring/types/observations";
import { uiText } from "pages/monitoring/outlets/initiatives/observations/layout/uiText";
import { translateTagCategory } from "pages/monitoring/outlets/tagsAdmin/utils/tagCategoryTranslator";

export function ObservationSmallCard({
  observation,
}: {
  observation: ObservationMetadata;
}) {
  const { currentObservation } = useObservationsCTX();
  const isCurrent =
    currentObservation && observation.id === currentObservation.observationId;

  const tagsGrouped = (observation.tags || []).reduce<
    Record<number, { group: string; tags: string[] }>
  >((all, tag) => {
    if (!all[tag.tag.category.id]) {
      all[tag.tag.category.id] = {
        group: translateTagCategory(tag.tag.category.name),
        tags: [],
      };
    }
    all[tag.tag.category.id].tags.push(tag.tag.name);

    return all;
  }, {});

  const { since, until } = useMemo(
    () =>
      observation.versions.length === 1
        ? {
            since: new Date(observation.versions[0].creationDate),
            until: null,
          }
        : observation.versions.reduce(
            (acc, v) => {
              const current = new Date(v.creationDate);
              if (current < acc.since) {
                acc.since = current;
              }
              if (current > acc.until) {
                acc.until = current;
              }
              return acc;
            },
            {
              since: new Date(observation.versions[0].creationDate),
              until: new Date(observation.versions[0].creationDate),
            },
          ),
    [observation.versions],
  );

  return (
    <div
      className={cn(
        "p-3 pb-2 rounded-xl outline outline-transparent hover:outline-primary transition-colors duration-300",
        isCurrent ? "bg-muted outline-primary" : "bg-background shadow-2xl",
      )}
    >
      <h4 className="mb-1">{observation.name}</h4>
      <div className="flex flex-wrap m-1 ml-0 gap-2">
        {Object.values(tagsGrouped).map((tags, i) => {
          const colorValues = TAG_COLORS[i % TAG_COLORS.length];
          const colorSet = `${colorValues.bg} ${colorValues.fg}`;

          return (
            <TagsRender
              key={`tags_${tags.group}_${i}`}
              tags={tags.tags}
              srTitle={tags.group}
              className={cn(colorSet, "font-normal")}
            />
          );
        })}
      </div>

      <hr className="mt-2 border-grey-light" />

      {until === null ? (
        <div className="flex gap-2 justify-between p-1 rounded items-center hover:bg-muted">
          <div className="text-sm flex flex-col *:m-0!">
            <span>{uiText.search.card.singleVersion.title}</span>
            <time dateTime={since.toISOString()}>
              {since.toLocaleDateString(LOCALE)}
            </time>
          </div>
          {!isCurrent && (
            <Button variant="ghost-clean" className="px-1!" asChild>
              <Link
                to={`/Monitoreo/Iniciativas/${observation.initiativeId}/Indicadores/${observation.versions[0].id}`}
                title={uiText.search.card.gotoBtn.title}
                aria-label={uiText.search.card.gotoBtn.sr}
              >
                {uiText.search.card.gotoBtn.label}
                <ChevronRightCircle />
              </Link>
            </Button>
          )}
        </div>
      ) : (
        <div>
          <dl className="text-sm mb-2 flex justify-between gap-2 *:m-0! *:flex *:flex-col text-center *:items-center">
            <div>
              <dt>{uiText.search.card.nVersions.title}</dt>
              <dd className="font-normal">{observation.versions.length}</dd>
            </div>

            <div>
              <dt>{uiText.search.card.nVersions.first}</dt>
              <dd className="font-normal">
                <time dateTime={since.toISOString()}>
                  {since.toLocaleDateString(LOCALE)}
                </time>
              </dd>
            </div>

            <div>
              <dt>{uiText.search.card.nVersions.last}</dt>
              <dd className="font-normal">
                <time dateTime={until.toISOString()}>
                  {until.toLocaleDateString(LOCALE)}
                </time>
              </dd>
            </div>
          </dl>

          {observation.versions
            .toSorted((a, b) => a.version - b.version)
            .map((v) => {
              const date = new Date(v.creationDate);

              return (
                <div
                  key={`version_${observation.id}_${v.version}`}
                  className="flex gap-2 justify-between p-1 rounded items-center hover:bg-muted"
                >
                  <div className="text-sm flex flex-col *:m-0!">
                    <span>Version {v.version}</span>
                    <time dateTime={date.toISOString()}>
                      {date.toLocaleDateString(LOCALE)}
                    </time>
                  </div>
                  {!(isCurrent && currentObservation.version === v.version) && (
                    <Button variant="ghost-clean" className="px-1!" asChild>
                      <Link
                        to={`/Monitoreo/Iniciativas/${observation.initiativeId}/Indicadores/${v.id}`}
                        title={uiText.search.card.gotoBtn.title}
                        aria-label={uiText.search.card.gotoBtn.sr}
                      >
                        {uiText.search.card.gotoBtn.label}
                        <ChevronRightCircle />
                      </Link>
                    </Button>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
